import { useEffect } from 'preact/hooks';
import { signal } from '@preact/signals';
import { api } from '../api/client';
import { T } from '../styles/tokens';
import { Button, Input, Select, ListSkeleton, SkeletonBar } from '../components/ui';

// ═══════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════

interface Plan {
  id: string; name: string; slug: string; description: string;
  price_usd_monthly: number; price_usd_yearly: number;
  price_brl_monthly: number; price_brl_yearly: number;
  stripe_price_id_monthly_usd: string; stripe_price_id_yearly_usd: string;
  stripe_price_id_monthly_brl: string; stripe_price_id_yearly_brl: string;
  workspaces_limit: number;
  cpu_per_workspace: number; ram_mb_per_workspace: number;
  max_agents_per_workspace: number; max_skills: number;
  features: string; referral_limit: number; sort_order: number;
  active: number; is_free: number; grace_period_days: number;
  overage_allowed: number; overage_rate: number;
  white_label_enabled: number; custom_domain_enabled: number;
  custom_branding_enabled: number; custom_instances_enabled: number;
  // Consolidated resource fields (from plan_instance_configs)
  memory_request: string; memory_limit: string;
  cpu_request: string; cpu_limit: string;
  auto_suspend_enabled: number; auto_suspend_minutes: number;
  auto_resume_enabled: number; daily_hours_limit: number;
  max_replicas: number; storage_size: string;
  // Premium differentiators
  llm_discount_pct: number; backup_frequency: string;
  backup_retention_days: number; queue_priority: string;
  rate_limit_rpm: number; support_tier: string;
  log_retention_days: number; model_access: string;
  created_at: string; updated_at: string;
}

interface Sub {
  id: string; customer_id: string; customer_email?: string; customer_name?: string;
  plan: string; plan_id: string; status: string; billing_cycle: string;
  stripe_subscription_id: string; stripe_customer_id: string;
  current_period_start: string; current_period_end: string;
  cancel_at_period_end: number; canceled_at: string;
  workspaces_limit: number; workspaces_used: number;
  grace_period_end: string; overage_total_usd: number;
  trial_end: string; created_at: string; updated_at: string;
}

interface Metrics {
  mrr: number; arr: number; arpu: number; ltv: number;
  churn_rate: number; conversion_free_to_paid: number;
  total_active: number; total_active_paid: number; total_free: number;
  total_canceled_30d: number; revenue_by_gateway: Record<string, number>;
  monthly_revenue: { month: string; total: number }[];
}

interface Payment {
  id: string; customer_id: string; customer_email?: string; customer_name?: string;
  amount: number; currency: string; status: string;
  payment_gateway: string; payment_id: string;
  created_at: string; updated_at: string;
}

interface Coupon {
  id: string; code: string; description: string;
  discount_type: string; discount_value: number;
  max_uses: number | null; used_count: number;
  valid_from: string; valid_until: string;
  applicable_plans: string; active: number;
  created_at: string; updated_at: string;
}

// ═══════════════════════════════════════════════════════════
// State
// ═══════════════════════════════════════════════════════════

const activeTab = signal(0);
const loading = signal(false);

const plans = signal<Plan[]>([]);
const editingPlan = signal<Plan | null>(null);
const showPlanModal = signal(false);

const subs = signal<Sub[]>([]);
const subsTotal = signal(0);
const filterStatus = signal('');
const filterPlan = signal('');
const searchEmail = signal('');
const editingSub = signal<Sub | null>(null);
const showSubModal = signal(false);

const metrics = signal<Metrics | null>(null);

const payments = signal<Payment[]>([]);
const paymentsTotal = signal(0);
const paymentsTotalReceived = signal(0);
const payFilterGateway = signal('');
const payFilterStatus = signal('');

const config = signal<Record<string, string>>({});
const coupons = signal<Coupon[]>([]);
const editingCoupon = signal<Coupon | null>(null);
const showCouponModal = signal(false);

// ═══════════════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════════════

const S = {
  page: { padding: '24px', maxWidth: 1400, margin: '0 auto' } as const,
  card: { background: T.bgSecondary, borderRadius: 12, padding: 20, border: `1px solid ${T.border}` } as const,
  cardSm: { background: T.bgSecondary, borderRadius: 12, padding: 16, border: `1px solid ${T.border}` } as const,
  title: { color: T.text, fontSize: '1.5rem', fontWeight: 800, marginBottom: 20, letterSpacing: '-0.02em' } as const,
  label: { color: T.textSecondary, fontSize: '0.7rem', textTransform: 'uppercase' as const, letterSpacing: '0.05em', fontWeight: 500 },
  input: { background: T.bgCardHover, color: T.text, border: `1px solid ${T.borderHover}`, borderRadius: 8, padding: '8px 12px', width: '100%', fontSize: '0.85rem', outline: 'none' } as const,
  select: { background: T.bgHover, color: T.text, border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 12px', outline: 'none' } as const,
  btn: { background: T.primary, color: T.text, border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' } as const,
  btnDanger: { background: T.danger, color: T.text, border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' } as const,
  btnGhost: { background: 'transparent', color: T.textSecondary, border: `1px solid ${T.borderHover}`, borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: '0.85rem' } as const,
  th: { padding: '10px 12px', textAlign: 'left' as const, color: T.textSecondary, fontSize: '0.7rem', textTransform: 'uppercase' as const, letterSpacing: '0.05em', fontWeight: 500 },
  td: { padding: '10px 12px', color: T.textSecondary, fontSize: '0.85rem' } as const,
  overlay: { position: 'fixed' as const, top: 0, left: 0, right: 0, bottom: 0, background: T.bgOverlay, backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: T.bg, borderRadius: 16, padding: 24, border: `1px solid ${T.border}`, width: '90%', maxWidth: 680, maxHeight: '85vh', overflowY: 'auto' as const },
  sectionHeader: { color: T.textSecondary, fontSize: '0.7rem', textTransform: 'uppercase' as const, letterSpacing: '0.5px', fontWeight: 600, paddingBottom: 8, marginBottom: 12, marginTop: 20, borderBottom: `1px solid ${T.border}` } as const,
};

const badge = (status: string) => {
  const colors: Record<string, { bg: string; fg: string }> = {
    active: { bg: T.successBg, fg: T.success },
    past_due: { bg: T.warningBg, fg: T.warning },
    suspended: { bg: T.dangerBg, fg: T.danger },
    canceled: { bg: T.borderHover, fg: T.textMuted },
    completed: { bg: T.successBg, fg: T.success },
    pending_payment: { bg: T.warningBg, fg: T.warning },
    failed: { bg: T.dangerBg, fg: T.danger },
  };
  const c = colors[status] || colors.canceled;
  return { padding: '2px 8px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 600, background: c.bg, color: c.fg, display: 'inline-block' };
};

const planColor = (slug: string) => {
  if (slug.includes('free')) return T.textMuted;
  if (slug.includes('pro')) return T.primary;
  if (slug.includes('business')) return T.warning;
  if (slug.includes('enterprise')) return T.danger;
  return T.textSecondary;
};

// ═══════════════════════════════════════════════════════════
// Loaders
// ═══════════════════════════════════════════════════════════

async function loadPlans() {
  try {
    const r = await api.get<{ plans: Plan[] }>('/admin/subscription-plans');
    plans.value = r.plans || [];
  } catch { /* */ }
}

async function loadSubs() {
  loading.value = true;
  try {
    const p = new URLSearchParams();
    if (filterStatus.value) p.set('status', filterStatus.value);
    if (filterPlan.value) p.set('plan', filterPlan.value);
    if (searchEmail.value) p.set('search', searchEmail.value);
    const r = await api.get<{ subscriptions: Sub[]; total: number }>(`/admin/subscriptions?${p}`);
    subs.value = r.subscriptions || [];
    subsTotal.value = r.total || 0;
  } catch { /* */ }
  loading.value = false;
}

async function loadMetrics() {
  try {
    const r = await api.get<Metrics>('/admin/subscriptions/metrics-advanced');
    metrics.value = r;
  } catch { /* */ }
}

async function loadPayments() {
  try {
    const p = new URLSearchParams();
    if (payFilterGateway.value) p.set('gateway', payFilterGateway.value);
    if (payFilterStatus.value) p.set('status', payFilterStatus.value);
    const r = await api.get<{ payments: Payment[]; total: number; total_received: number }>(`/admin/subscriptions/payments?${p}`);
    payments.value = r.payments || [];
    paymentsTotal.value = r.total || 0;
    paymentsTotalReceived.value = r.total_received || 0;
  } catch { /* */ }
}

async function loadConfig() {
  try {
    const r = await api.get<{ config: Record<string, string> }>('/admin/subscriptions/config');
    config.value = r.config || {};
    const r2 = await api.get<{ coupons: Coupon[] }>('/admin/subscriptions/coupons');
    coupons.value = r2.coupons || [];
  } catch { /* */ }
}

// ═══════════════════════════════════════════════════════════
// Actions
// ═══════════════════════════════════════════════════════════

async function savePlan(plan: Partial<Plan> & { slug: string; name: string }, isNew: boolean) {
  try {
    if (isNew) {
      await api.post('/admin/subscription-plans', plan);
    } else {
      await api.put(`/admin/subscription-plans/${plan.id || plan.slug}`, plan);
    }
    showPlanModal.value = false;
    editingPlan.value = null;
    await loadPlans();
  } catch { alert('Erro ao salvar plano'); }
}

async function togglePlanActive(plan: Plan) {
  await api.put(`/admin/subscription-plans/${plan.id}`, { active: plan.active ? 0 : 1 });
  await loadPlans();
}

async function updateSub(id: string, data: Record<string, unknown>) {
  await api.put(`/admin/subscriptions/${id}`, data);
  await loadSubs();
}

async function addCredits(id: string) {
  const amount = prompt('Quantidade de creditos LLM a adicionar (USD):');
  if (!amount) return;
  await api.post(`/admin/subscriptions/${id}/add-credits`, { amount: parseFloat(amount), reason: 'Admin manual' });
  showSubModal.value = false;
  await loadSubs();
}

async function extendPeriod(id: string) {
  const days = prompt('Quantos dias estender:');
  if (!days) return;
  await api.post(`/admin/subscriptions/${id}/extend`, { days: parseInt(days) });
  showSubModal.value = false;
  await loadSubs();
}

async function saveConfig() {
  await api.put('/admin/subscriptions/config', { config: config.value });
  alert('Configuracao salva!');
}

async function saveCoupon(coupon: Partial<Coupon>, isNew: boolean) {
  if (isNew) {
    await api.post('/admin/subscriptions/coupons', coupon);
  } else {
    await api.put(`/admin/subscriptions/coupons/${coupon.id}`, coupon);
  }
  showCouponModal.value = false;
  editingCoupon.value = null;
  await loadConfig();
}

async function deleteCoupon(id: string) {
  if (!confirm('Deletar cupom?')) return;
  await api.delete(`/admin/subscriptions/coupons/${id}`);
  await loadConfig();
}

// ═══════════════════════════════════════════════════════════
// Shared Components
// ═══════════════════════════════════════════════════════════

function Field({ label, value, onChange, type, disabled, full }: { label: string; value: any; onChange: (v: string) => void; type?: string; disabled?: boolean; full?: boolean }) {
  return (
    <div style={full ? { gridColumn: '1 / -1' } : {}}>
      <div style={{ ...S.label, marginBottom: 4 }}>{label}</div>
      <Input type={type || 'text'} value={String(value ?? '')} disabled={disabled}
        onChange={onChange}
        style={{ opacity: disabled ? 0.5 : 1 }} />
    </div>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: T.textSecondary, fontSize: '0.85rem' }}>
      <div onClick={() => onChange(!checked)} style={{
        width: 44, height: 24, borderRadius: 12, position: 'relative',
        background: checked ? T.success : T.textPrimary, transition: 'background 0.2s', cursor: 'pointer', flexShrink: 0,
      }}>
        <div style={{
          width: 18, height: 18, borderRadius: 9, background: '#fff', position: 'absolute', top: 3,
          left: checked ? 23 : 3, transition: 'left 0.2s',
        }} />
      </div>
      {label}
    </label>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={S.cardSm}>
      <div style={S.label}>{label}</div>
      <div style={{ color, fontSize: '1.6rem', fontWeight: 800, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function InfoRow({ label, value, badge: isBadge }: { label: string; value: string; badge?: boolean }) {
  return (
    <div>
      <div style={S.label}>{label}</div>
      {isBadge ? (
        <span style={badge(value)}>{value}</span>
      ) : (
        <div style={{ color: T.textSecondary, fontSize: '0.85rem', marginTop: 2, wordBreak: 'break-all' }}>{value}</div>
      )}
    </div>
  );
}

function SectionHeader({ title }: { title: string }) {
  return <div style={S.sectionHeader}>{title}</div>;
}

// ═══════════════════════════════════════════════════════════
// Tabs
// ═══════════════════════════════════════════════════════════

function Tabs() {
  const tabs = ['Planos', 'Assinaturas', 'Metricas', 'Pagamentos', 'Config'];
  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: `1px solid ${T.border}`, paddingBottom: 0, overflowX: 'auto' }}>
      {tabs.map((t, i) => (
        <Button key={i} onClick={() => { activeTab.value = i; }}
          variant={activeTab.value === i ? 'secondary' : 'ghost'}
          size="sm"
          style={{
            background: activeTab.value === i ? T.bgHover : 'transparent',
            color: activeTab.value === i ? T.text : T.textMuted,
            borderBottom: activeTab.value === i ? `2px solid ${T.primary}` : '2px solid transparent',
            borderRadius: '8px 8px 0 0', whiteSpace: 'nowrap',
          }}>{t}</Button>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TAB 1: Plans
// ═══════════════════════════════════════════════════════════

function PlanTab() {
  const featureKeys = ['dev_hub', 'api_access', 'custom_llm_providers', 'terminal_access', 'instance_config', 'orchestrator_access', 'orchestrator_gateway_token', 'custom_domain', 'custom_branding', 'custom_instances', 'priority_support', 'sso', 'advanced_analytics', 'white_label', 'dedicated_infra', 'backup_weekly', 'backup_daily', 'backup_hourly'];

  const parseFeatures = (f: string) => { try { return JSON.parse(f || '{}'); } catch { return {}; } };

  const emptyPlan: Plan = {
    id: '', name: '', slug: '', description: '',
    price_usd_monthly: 0, price_usd_yearly: 0, price_brl_monthly: 0, price_brl_yearly: 0,
    stripe_price_id_monthly_usd: '', stripe_price_id_yearly_usd: '',
    stripe_price_id_monthly_brl: '', stripe_price_id_yearly_brl: '',
    workspaces_limit: 1, cpu_per_workspace: 0.5, ram_mb_per_workspace: 256,
    max_agents_per_workspace: 1, max_skills: 3, features: '{}', referral_limit: 10,
    sort_order: 0, active: 1, is_free: 0, grace_period_days: 3,
    overage_allowed: 0, overage_rate: 1,
    white_label_enabled: 0, custom_domain_enabled: 0, custom_branding_enabled: 0, custom_instances_enabled: 0,
    created_at: '', updated_at: '',
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ color: T.text, fontSize: '1.1rem', fontWeight: 700 }}>Planos de Assinatura</h2>
        <Button variant="primary" size="sm" onClick={() => { editingPlan.value = { ...emptyPlan }; showPlanModal.value = true; }}>Novo Plano</Button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.border}` }}>
              {['Nome', 'Slug', 'BRL/mes', 'USD/mes', 'Workspaces', 'Agents', 'Skills', 'Status', 'Acoes'].map(h => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {plans.value.map((p, idx) => (
              <tr key={p.id} style={{ background: idx % 2 === 0 ? T.bgInput : T.bgCard, borderBottom: `1px solid ${T.bgCard}` }}>
                <td style={S.td}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 4, background: planColor(p.slug), flexShrink: 0 }} />
                    <span style={{ color: T.text, fontWeight: 600 }}>{p.name}</span>
                  </div>
                </td>
                <td style={{ ...S.td, color: T.textMuted, fontFamily: 'monospace', fontSize: '0.8rem' }}>{p.slug}</td>
                <td style={S.td}>R${p.price_brl_monthly}</td>
                <td style={S.td}>${p.price_usd_monthly}</td>
                <td style={S.td}>{p.workspaces_limit}</td>
                <td style={S.td}>{p.max_agents_per_workspace}</td>
                <td style={S.td}>{p.max_skills}</td>
                <td style={S.td}>
                  <span style={badge(p.active ? 'active' : 'canceled')}>{p.active ? 'Ativo' : 'Inativo'}</span>
                </td>
                <td style={S.td}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Button variant="ghost" size="xs" onClick={() => { editingPlan.value = { ...p }; showPlanModal.value = true; }}>Editar</Button>
                    <Button variant="ghost" size="xs" style={{ color: p.active ? T.danger : T.success }} onClick={() => togglePlanActive(p)}>{p.active ? 'Desativar' : 'Ativar'}</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Plan Edit Modal */}
      {showPlanModal.value && editingPlan.value && (() => {
        const p = editingPlan.value!;
        const isNew = !p.id || !p.created_at;
        const feat = parseFeatures(p.features);
        const set = (k: keyof Plan, v: any) => { editingPlan.value = { ...editingPlan.value!, [k]: v }; };
        const setFeat = (k: string, v: boolean) => {
          const f = { ...parseFeatures(editingPlan.value!.features), [k]: v };
          set('features', JSON.stringify(f));
        };

        return (
          <div style={S.overlay} onClick={(e) => { if (e.target === e.currentTarget) showPlanModal.value = false; }}>
            <div style={S.modal}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <h3 style={{ color: T.text, fontSize: '1.1rem', fontWeight: 700 }}>{isNew ? 'Novo Plano' : `Editar: ${p.name}`}</h3>
                <Button variant="ghost" size="xs" onClick={() => showPlanModal.value = false} style={{ fontSize: '1.2rem', padding: 4 }}>x</Button>
              </div>

              {/* Section 1: Basic Info */}
              <SectionHeader title="Informacoes Basicas" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Nome" value={p.name} onChange={v => set('name', v)} />
                <Field label="Slug" value={p.slug} onChange={v => set('slug', v)} disabled={!isNew} />
                <Field label="Descricao" value={p.description || ''} onChange={v => set('description', v)} full />
              </div>
              <div style={{ display: 'flex', gap: 20, marginTop: 12 }}>
                <Toggle label="Ativo" checked={!!p.active} onChange={v => set('active', v ? 1 : 0)} />
                <Toggle label="Plano Gratuito" checked={!!p.is_free} onChange={v => set('is_free', v ? 1 : 0)} />
              </div>

              {/* Section 2: Pricing */}
              <SectionHeader title="Precos" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="USD / mes" value={p.price_usd_monthly} onChange={v => set('price_usd_monthly', parseFloat(v) || 0)} type="number" />
                <Field label="USD / ano" value={p.price_usd_yearly} onChange={v => set('price_usd_yearly', parseFloat(v) || 0)} type="number" />
                <Field label="BRL / mes" value={p.price_brl_monthly} onChange={v => set('price_brl_monthly', parseFloat(v) || 0)} type="number" />
                <Field label="BRL / ano" value={p.price_brl_yearly} onChange={v => set('price_brl_yearly', parseFloat(v) || 0)} type="number" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                <Field label="Stripe Price ID (USD Monthly)" value={p.stripe_price_id_monthly_usd || ''} onChange={v => set('stripe_price_id_monthly_usd', v)} />
                <Field label="Stripe Price ID (USD Yearly)" value={p.stripe_price_id_yearly_usd || ''} onChange={v => set('stripe_price_id_yearly_usd', v)} />
                <Field label="Stripe Price ID (BRL Monthly)" value={p.stripe_price_id_monthly_brl || ''} onChange={v => set('stripe_price_id_monthly_brl', v)} />
                <Field label="Stripe Price ID (BRL Yearly)" value={p.stripe_price_id_yearly_brl || ''} onChange={v => set('stripe_price_id_yearly_brl', v)} />
              </div>

              {/* Section 3: Limits */}
              <SectionHeader title="Limites e Recursos" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Workspaces" value={p.workspaces_limit} onChange={v => set('workspaces_limit', parseInt(v) || 0)} type="number" />
                <Field label="Max Agentes / WS" value={p.max_agents_per_workspace} onChange={v => set('max_agents_per_workspace', parseInt(v) || 0)} type="number" />
                <Field label="Max Skills" value={p.max_skills} onChange={v => set('max_skills', parseInt(v) || 0)} type="number" />
                <Field label="CPU / Workspace" value={p.cpu_per_workspace} onChange={v => set('cpu_per_workspace', parseFloat(v) || 0)} type="number" />
                <Field label="RAM MB / Workspace" value={p.ram_mb_per_workspace} onChange={v => set('ram_mb_per_workspace', parseInt(v) || 0)} type="number" />
                <Field label="Referral Limit" value={p.referral_limit} onChange={v => set('referral_limit', parseInt(v) || 0)} type="number" />
                <Field label="Grace Period (dias)" value={p.grace_period_days} onChange={v => set('grace_period_days', parseInt(v) || 0)} type="number" />
              </div>
              <div style={{ display: 'flex', gap: 20, marginTop: 12, alignItems: 'center' }}>
                <Toggle label="Overage Permitido" checked={!!p.overage_allowed} onChange={v => set('overage_allowed', v ? 1 : 0)} />
                <div style={{ width: 160 }}>
                  <Field label="Overage Rate" value={p.overage_rate} onChange={v => set('overage_rate', parseFloat(v) || 0)} type="number" />
                </div>
              </div>

              {/* Section 4: Capabilities (DB columns) */}
              <SectionHeader title="Funcionalidades" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <div style={{ color: T.textMuted, fontSize: '0.7rem', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase' as const }}>Infraestrutura</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <Toggle label="Custom Instances" checked={!!p.custom_instances_enabled} onChange={v => set('custom_instances_enabled', v ? 1 : 0)} />
                    <Toggle label="Custom Domain" checked={!!p.custom_domain_enabled} onChange={v => set('custom_domain_enabled', v ? 1 : 0)} />
                  </div>
                </div>
                <div>
                  <div style={{ color: T.textMuted, fontSize: '0.7rem', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase' as const }}>Personalizacao</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <Toggle label="White Label" checked={!!p.white_label_enabled} onChange={v => set('white_label_enabled', v ? 1 : 0)} />
                    <Toggle label="Custom Branding" checked={!!p.custom_branding_enabled} onChange={v => set('custom_branding_enabled', v ? 1 : 0)} />
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <Field label="Sort Order" value={p.sort_order} onChange={v => set('sort_order', parseInt(v) || 0)} type="number" />
              </div>

              {/* Section 5: Instance Resources (K8s) */}
              <SectionHeader title="Recursos da Instancia (K8s)" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Memory Request" value={p.memory_request || '128Mi'} onChange={v => set('memory_request', v)} />
                <Field label="Memory Limit" value={p.memory_limit || '1536Mi'} onChange={v => set('memory_limit', v)} />
                <Field label="CPU Request" value={p.cpu_request || '100m'} onChange={v => set('cpu_request', v)} />
                <Field label="CPU Limit" value={p.cpu_limit || '500m'} onChange={v => set('cpu_limit', v)} />
                <Field label="Max Replicas" value={p.max_replicas ?? 1} onChange={v => set('max_replicas', parseInt(v) || 1)} type="number" />
                <Field label="Storage" value={p.storage_size || '1Gi'} onChange={v => set('storage_size', v)} />
              </div>
              <div style={{ display: 'flex', gap: 20, marginTop: 12, flexWrap: 'wrap' }}>
                <Toggle label="Auto-Suspend" checked={!!p.auto_suspend_enabled} onChange={v => set('auto_suspend_enabled', v ? 1 : 0)} />
                <div style={{ width: 140 }}>
                  <Field label="Suspend (min)" value={p.auto_suspend_minutes ?? 30} onChange={v => set('auto_suspend_minutes', parseInt(v) || 0)} type="number" />
                </div>
                <Toggle label="Auto-Resume" checked={!!p.auto_resume_enabled} onChange={v => set('auto_resume_enabled', v ? 1 : 0)} />
                <div style={{ width: 140 }}>
                  <Field label="Limite Diario (h)" value={p.daily_hours_limit ?? 0} onChange={v => set('daily_hours_limit', parseInt(v) || 0)} type="number" />
                </div>
              </div>

              {/* Section 6: Premium Differentiators */}
              <SectionHeader title="Diferenciadores Premium" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Desconto LLM (%)" value={p.llm_discount_pct ?? 0} onChange={v => set('llm_discount_pct', parseInt(v) || 0)} type="number" />
                <Field label="Rate Limit (RPM)" value={p.rate_limit_rpm ?? 30} onChange={v => set('rate_limit_rpm', parseInt(v) || 0)} type="number" />
                <Field label="Backup Frequencia" value={p.backup_frequency || 'none'} onChange={v => set('backup_frequency', v)} />
                <Field label="Backup Retencao (dias)" value={p.backup_retention_days ?? 0} onChange={v => set('backup_retention_days', parseInt(v) || 0)} type="number" />
                <Field label="Prioridade na Fila" value={p.queue_priority || 'normal'} onChange={v => set('queue_priority', v)} />
                <Field label="Suporte" value={p.support_tier || 'community'} onChange={v => set('support_tier', v)} />
                <Field label="Retencao de Logs (dias)" value={p.log_retention_days ?? 7} onChange={v => set('log_retention_days', parseInt(v) || 0)} type="number" />
                <Field label="Acesso a Modelos" value={p.model_access || 'all'} onChange={v => set('model_access', v)} />
              </div>

              {/* Section 7: Features JSON */}
              <SectionHeader title="Features (JSON)" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {featureKeys.map(k => (
                  <Toggle key={k} label={k.replace(/_/g, ' ')} checked={!!feat[k]} onChange={v => setFeat(k, v)} />
                ))}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end', borderTop: `1px solid ${T.border}`, paddingTop: 16 }}>
                <Button variant="ghost" size="sm" onClick={() => showPlanModal.value = false}>Cancelar</Button>
                <Button variant="primary" size="sm" onClick={() => savePlan(editingPlan.value! as any, isNew)}>Salvar</Button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TAB 2: Subscriptions
// ═══════════════════════════════════════════════════════════

function SubTab() {
  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <Select value={filterStatus.value} onChange={(v) => { filterStatus.value = v; loadSubs(); }} style={S.select} options={[{ value: '', label: 'Todos os status' }, { value: 'active', label: 'Ativo' }, { value: 'past_due', label: 'Vencido' }, { value: 'suspended', label: 'Suspenso' }, { value: 'canceled', label: 'Cancelado' }]} />
        <Select value={filterPlan.value} onChange={(v) => { filterPlan.value = v; loadSubs(); }} style={S.select} options={[{ value: '', label: 'Todos os planos' }, ...plans.value.map(p => ({ value: p.slug, label: p.name }))]} />
        <input placeholder="Buscar por email..." value={searchEmail.value}
          onInput={(e) => { searchEmail.value = (e.target as HTMLInputElement).value; }}
          onKeyDown={(e) => { if (e.key === 'Enter') loadSubs(); }}
          style={{ ...S.input, width: 220 }} />
        <Button variant="primary" size="sm" onClick={() => loadSubs()}>Buscar</Button>
        <span style={{ color: T.textMuted, fontSize: '0.8rem', marginLeft: 'auto' }}>{subsTotal.value} total</span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.border}` }}>
              {['Cliente', 'Email', 'Plano', 'Status', 'Ciclo', 'LLM Usado', 'WS', 'Criado'].map(h => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {subs.value.map((sub, idx) => (
              <tr key={sub.id} style={{ background: idx % 2 === 0 ? T.bgInput : T.bgCard, cursor: 'pointer', borderBottom: `1px solid ${T.bgCard}` }}
                onClick={() => { editingSub.value = sub; showSubModal.value = true; }}>
                <td style={S.td}>{sub.customer_name || sub.customer_id?.slice(0, 8) + '...'}</td>
                <td style={{ ...S.td, color: T.primary }}>{sub.customer_email || '-'}</td>
                <td style={{ ...S.td, color: T.text, fontWeight: 600 }}>{sub.plan_id || sub.plan}</td>
                <td style={S.td}><span style={badge(sub.status)}>{sub.status}</span></td>
                <td style={S.td}>{sub.billing_cycle}</td>
                <td style={S.td}>{sub.workspaces_used || 0}/{sub.workspaces_limit || 0}</td>
                <td style={{ ...S.td, color: T.textMuted, fontSize: '0.8rem' }}>{sub.created_at ? new Date(sub.created_at).toLocaleDateString('pt-BR') : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showSubModal.value && editingSub.value && (() => {
        const s = editingSub.value!;
        return (
          <div style={S.overlay} onClick={(e) => { if (e.target === e.currentTarget) showSubModal.value = false; }}>
            <div style={S.modal}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ color: T.text, fontSize: '1.1rem', fontWeight: 700 }}>
                  Assinatura: {s.customer_email || s.customer_name || s.customer_id?.slice(0, 12)}
                </h3>
                <Button variant="ghost" size="xs" onClick={() => showSubModal.value = false} style={{ fontSize: '1.2rem', padding: 4 }}>x</Button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <InfoRow label="ID" value={s.id} />
                <InfoRow label="Customer ID" value={s.customer_id} />
                <InfoRow label="Email" value={s.customer_email || '-'} />
                <InfoRow label="Nome" value={s.customer_name || '-'} />
                <InfoRow label="Plano" value={s.plan_id || s.plan} />
                <InfoRow label="Status" value={s.status} badge />
                <InfoRow label="Ciclo" value={s.billing_cycle} />
                <InfoRow label="Stripe Sub ID" value={s.stripe_subscription_id || '-'} />
                <InfoRow label="Workspaces" value={`${s.workspaces_used}/${s.workspaces_limit}`} />
                <InfoRow label="Periodo" value={s.current_period_end ? new Date(s.current_period_end).toLocaleDateString('pt-BR') : '-'} />
                <InfoRow label="Overage" value={`$${s.overage_total_usd?.toFixed(2) || '0.00'}`} />
                <InfoRow label="Grace Period" value={s.grace_period_end ? new Date(s.grace_period_end).toLocaleDateString('pt-BR') : '-'} />
                <InfoRow label="Cancelado em" value={s.canceled_at ? new Date(s.canceled_at).toLocaleDateString('pt-BR') : '-'} />
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
                <Button variant="primary" size="sm" onClick={() => addCredits(s.id)}>Adicionar Creditos</Button>
                <Button variant="primary" size="sm" onClick={() => extendPeriod(s.id)}>Estender Periodo</Button>
                {s.status === 'active' && (
                  <Button variant="danger" size="sm" onClick={async () => { await updateSub(s.id, { status: 'suspended' }); showSubModal.value = false; }}>Suspender</Button>
                )}
                {s.status === 'suspended' && (
                  <Button variant="success" size="sm" onClick={async () => { await updateSub(s.id, { status: 'active' }); showSubModal.value = false; }}>Reativar</Button>
                )}
                {s.status !== 'canceled' && (
                  <Button variant="danger" size="sm" onClick={async () => { if (confirm('Cancelar assinatura?')) { await updateSub(s.id, { status: 'canceled', canceled_at: new Date().toISOString() }); showSubModal.value = false; } }}>Cancelar</Button>
                )}
                <Select style={S.select} value="" onChange={async (v) => {
                  if (v) { await updateSub(s.id, { plan: v, plan_id: v }); showSubModal.value = false; }
                }} placeholder="Trocar plano..." options={plans.value.map(p => ({ value: p.slug, label: p.name }))} />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <Button variant="ghost" size="sm" onClick={() => showSubModal.value = false}>Fechar</Button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TAB 3: Metrics
// ═══════════════════════════════════════════════════════════

function MetricsTab() {
  const m = metrics.value;
  if (!m) return <ListSkeleton rows={2} />;

  const maxRevenue = Math.max(...(m.monthly_revenue.map(r => r.total) || [1]), 1);

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <MetricCard label="MRR" value={`$${m.mrr.toFixed(2)}`} color={T.success} />
        <MetricCard label="ARR" value={`$${m.arr.toFixed(2)}`} color={T.primary} />
        <MetricCard label="ARPU" value={`$${m.arpu.toFixed(2)}`} color={T.purple} />
        <MetricCard label="LTV" value={`$${m.ltv.toFixed(2)}`} color={T.warning} />
        <MetricCard label="Churn Rate (30d)" value={`${m.churn_rate.toFixed(1)}%`} color={m.churn_rate > 10 ? T.danger : T.success} />
        <MetricCard label="Conversao Free > Paid" value={`${m.conversion_free_to_paid.toFixed(1)}%`} color="#06b6d4" />
        <MetricCard label="Ativos Total" value={String(m.total_active)} color="#d1d5db" />
        <MetricCard label="Ativos Pagos" value={String(m.total_active_paid)} color={T.success} />
        <MetricCard label="Free" value={String(m.total_free)} color={T.textMuted} />
        <MetricCard label="Cancelados (30d)" value={String(m.total_canceled_30d)} color={T.danger} />
      </div>

      <div style={{ ...S.card, marginBottom: 16 }}>
        <div style={{ ...S.label, marginBottom: 12 }}>Receita por Gateway</div>
        <div style={{ display: 'flex', gap: 24 }}>
          {Object.entries(m.revenue_by_gateway).length === 0 ? (
            <div style={{ color: T.textMuted }}>Sem dados</div>
          ) : Object.entries(m.revenue_by_gateway).map(([gw, total]) => (
            <div key={gw}>
              <div style={{ color: T.text, fontSize: '1.2rem', fontWeight: 700 }}>R${total.toFixed(2)}</div>
              <div style={{ color: T.textSecondary, fontSize: '0.8rem' }}>{gw}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ ...S.card }}>
        <div style={{ ...S.label, marginBottom: 12 }}>Receita Mensal (ultimos 6 meses)</div>
        {m.monthly_revenue.length === 0 ? (
          <div style={{ color: T.textMuted }}>Sem dados</div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 160, paddingTop: 8 }}>
            {m.monthly_revenue.map(r => (
              <div key={r.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div style={{ color: T.textSecondary, fontSize: '0.7rem' }}>R${r.total.toFixed(0)}</div>
                <div style={{
                  width: '100%', maxWidth: 48, borderRadius: 4,
                  background: 'linear-gradient(180deg, T.primary 0%, #1d4ed8 100%)',
                  height: `${Math.max((r.total / maxRevenue) * 120, 4)}px`,
                }} />
                <div style={{ color: T.textMuted, fontSize: '0.65rem' }}>{r.month}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TAB 4: Payments
// ═══════════════════════════════════════════════════════════

function PaymentsTab() {
  return (
    <div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ ...S.card, padding: '12px 20px' }}>
          <div style={S.label}>Total Recebido</div>
          <div style={{ color: T.success, fontSize: '1.4rem', fontWeight: 800 }}>R${paymentsTotalReceived.value.toFixed(2)}</div>
        </div>
        <Select value={payFilterGateway.value} onChange={(v) => { payFilterGateway.value = v; loadPayments(); }} style={S.select} options={[{ value: '', label: 'Todos gateways' }, { value: 'stripe', label: 'Stripe' }, { value: 'pagarme', label: 'Pagar.me' }]} />
        <Select value={payFilterStatus.value} onChange={(v) => { payFilterStatus.value = v; loadPayments(); }} style={S.select} options={[{ value: '', label: 'Todos status' }, { value: 'completed', label: 'Completo' }, { value: 'pending_payment', label: 'Pendente' }, { value: 'failed', label: 'Falhou' }]} />
        <span style={{ color: T.textMuted, fontSize: '0.8rem', marginLeft: 'auto' }}>{paymentsTotal.value} registros</span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.border}` }}>
              {['Cliente', 'Email', 'Valor', 'Moeda', 'Gateway', 'Status', 'Data'].map(h => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {payments.value.map((p, idx) => (
              <tr key={p.id} style={{ background: idx % 2 === 0 ? T.bgInput : T.bgCard, borderBottom: `1px solid ${T.bgCard}` }}>
                <td style={S.td}>{p.customer_name || p.customer_id?.slice(0, 8)}</td>
                <td style={{ ...S.td, color: T.primary }}>{p.customer_email || '-'}</td>
                <td style={{ ...S.td, color: T.text, fontWeight: 600 }}>{p.currency === 'BRL' ? 'R$' : '$'}{p.amount?.toFixed(2)}</td>
                <td style={S.td}>{p.currency}</td>
                <td style={S.td}><span style={badge(p.payment_gateway === 'stripe' ? 'active' : 'past_due')}>{p.payment_gateway || '-'}</span></td>
                <td style={S.td}><span style={badge(p.status)}>{p.status}</span></td>
                <td style={{ ...S.td, color: T.textMuted, fontSize: '0.8rem' }}>{p.created_at ? new Date(p.created_at).toLocaleDateString('pt-BR') : '-'}</td>
              </tr>
            ))}
            {payments.value.length === 0 && (
              <tr><td colSpan={7} style={{ ...S.td, textAlign: 'center', color: T.textMuted }}>Nenhum pagamento encontrado</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TAB 5: Config
// ═══════════════════════════════════════════════════════════

function ConfigTab() {
  const setConf = (k: string, v: string) => { config.value = { ...config.value, [k]: v }; };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <div style={S.card}>
          <h3 style={{ color: T.text, fontSize: '1rem', fontWeight: 700, marginBottom: 12 }}>Configuracoes Gerais</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ color: T.textSecondary, fontSize: 12, marginBottom: 4, display: 'block' }}>Plano Padrao (novo usuario)</label>
              <select
                value={config.value.default_new_user_plan || 'free'}
                onChange={(e: any) => setConf('default_new_user_plan', e.target.value)}
                style={{ width: '100%', padding: '8px 12px', background: T.border, border: `1px solid ${T.borderHover}`, borderRadius: 8, color: T.text, fontSize: 14 }}
              >
                <option value="none">Sem plano</option>
                {plans.value.map(p => (
                  <option key={p.slug} value={p.slug}>
                    {p.name} {!p.active ? '(inativo)' : ''} {p.is_free ? '(gratis)' : `($${p.price_usd_monthly}/mes)`}
                  </option>
                ))}
              </select>
            </div>
            <Field label="Saldo Inicial USD (novo usuario)" value={config.value.default_new_user_balance || '0'} onChange={v => setConf('default_new_user_balance', v)} type="number" />
            <Field label="Markup LLM Padrao (%)" value={config.value.llm_markup_pct || '30'} onChange={v => setConf('llm_markup_pct', v)} type="number" />
            <Field label="Taxa de Cambio (BRL/USD) Override" value={config.value.exchange_rate_brl_usd || ''} onChange={v => setConf('exchange_rate_brl_usd', v)} type="number" />
            <Field label="Trial Days Padrao" value={config.value.default_trial_days || '0'} onChange={v => setConf('default_trial_days', v)} type="number" />
            <Field label="Email Suporte" value={config.value.support_email || ''} onChange={v => setConf('support_email', v)} />
          </div>
          <Button variant="primary" size="sm" style={{ marginTop: 16 }} onClick={saveConfig}>Salvar Configuracoes</Button>
        </div>

        <div style={S.card}>
          <h3 style={{ color: T.text, fontSize: '1rem', fontWeight: 700, marginBottom: 12 }}>Trial por Plano (dias)</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {plans.value.filter(p => !p.is_free).map(p => (
              <Field key={p.slug} label={p.name} value={config.value[`trial_days_${p.slug}`] || '0'} onChange={v => setConf(`trial_days_${p.slug}`, v)} type="number" />
            ))}
          </div>
          <Button variant="primary" size="sm" style={{ marginTop: 12 }} onClick={saveConfig}>Salvar</Button>
        </div>
      </div>

      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ color: T.text, fontSize: '1rem', fontWeight: 700 }}>Cupons</h3>
          <Button variant="primary" size="sm" onClick={() => {
            editingCoupon.value = { id: '', code: '', description: '', discount_type: 'percentage', discount_value: 0, max_uses: null, used_count: 0, valid_from: '', valid_until: '', applicable_plans: '[]', active: 1, created_at: '', updated_at: '' } as Coupon;
            showCouponModal.value = true;
          }}>Novo Cupom</Button>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.border}` }}>
              {['Codigo', 'Descricao', 'Tipo', 'Valor', 'Usos', 'Validade', 'Status', 'Acoes'].map(h => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {coupons.value.map((c, idx) => (
              <tr key={c.id} style={{ background: idx % 2 === 0 ? T.bgInput : T.bgCard, borderBottom: `1px solid ${T.bgCard}` }}>
                <td style={{ ...S.td, color: T.text, fontWeight: 600 }}>{c.code}</td>
                <td style={S.td}>{c.description || '-'}</td>
                <td style={S.td}>{c.discount_type === 'percentage' ? '%' : 'Fixo'}</td>
                <td style={S.td}>{c.discount_type === 'percentage' ? `${c.discount_value}%` : `$${c.discount_value}`}</td>
                <td style={S.td}>{c.used_count}/{c.max_uses || 'ilim.'}</td>
                <td style={{ ...S.td, fontSize: '0.75rem' }}>{c.valid_until ? new Date(c.valid_until).toLocaleDateString('pt-BR') : 'ilim.'}</td>
                <td style={S.td}><span style={badge(c.active ? 'active' : 'canceled')}>{c.active ? 'Ativo' : 'Inativo'}</span></td>
                <td style={S.td}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <Button variant="ghost" size="xs" onClick={() => { editingCoupon.value = { ...c }; showCouponModal.value = true; }}>Editar</Button>
                    <Button variant="danger" size="xs" onClick={() => deleteCoupon(c.id)}>Remover</Button>
                  </div>
                </td>
              </tr>
            ))}
            {coupons.value.length === 0 && (
              <tr><td colSpan={8} style={{ ...S.td, textAlign: 'center', color: T.textMuted }}>Nenhum cupom</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showCouponModal.value && editingCoupon.value && (() => {
        const c = editingCoupon.value!;
        const isNew = !c.id || !c.created_at;
        const set = (k: keyof Coupon, v: any) => { editingCoupon.value = { ...editingCoupon.value!, [k]: v }; };

        return (
          <div style={S.overlay} onClick={(e) => { if (e.target === e.currentTarget) showCouponModal.value = false; }}>
            <div style={{ ...S.modal, maxWidth: 500 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ color: T.text }}>{isNew ? 'Novo Cupom' : `Editar: ${c.code}`}</h3>
                <Button variant="ghost" size="xs" onClick={() => showCouponModal.value = false} style={{ fontSize: '1.2rem', padding: 4 }}>x</Button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Field label="Codigo" value={c.code} onChange={v => set('code', v)} />
                <Field label="Descricao" value={c.description || ''} onChange={v => set('description', v)} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <div style={{ ...S.label, marginBottom: 4 }}>Tipo</div>
                    <Select value={c.discount_type} onChange={(v) => set('discount_type', v)} style={{ ...S.select, width: '100%' }} options={[{ value: 'percentage', label: 'Percentual' }, { value: 'fixed', label: 'Valor Fixo' }]} />
                  </div>
                  <Field label="Valor" value={c.discount_value} onChange={v => set('discount_value', parseFloat(v) || 0)} type="number" />
                </div>
                <Field label="Maximo de Usos (vazio = ilimitado)" value={c.max_uses || ''} onChange={v => set('max_uses', v ? parseInt(v) : null)} type="number" />
                <Field label="Valido Ate (YYYY-MM-DD)" value={c.valid_until || ''} onChange={v => set('valid_until', v)} />
                <Toggle label="Ativo" checked={!!c.active} onChange={v => set('active', v ? 1 : 0)} />
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 16, justifyContent: 'flex-end' }}>
                <Button variant="ghost" size="sm" onClick={() => showCouponModal.value = false}>Cancelar</Button>
                <Button variant="primary" size="sm" onClick={() => saveCoupon(editingCoupon.value! as any, isNew)}>Salvar</Button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════

export function AdminSubscriptions() {
  useEffect(() => {
    loadPlans();
    loadSubs();
    loadMetrics();
    loadPayments();
    loadConfig();
  }, []);

  return (
    <div style={S.page}>
      <h1 style={S.title}>Gestao de Assinaturas</h1>
      <Tabs />
      {activeTab.value === 0 && <PlanTab />}
      {activeTab.value === 1 && <SubTab />}
      {activeTab.value === 2 && <MetricsTab />}
      {activeTab.value === 3 && <PaymentsTab />}
      {activeTab.value === 4 && <ConfigTab />}
    </div>
  );
}
